<template>
  <div>
    <v-dialog
      v-model="showDialog"
      width="400px"
      persistent
    >
      <v-card>
        <v-toolbar
          color="primaryLight"
          dark
          dense
        >
          <v-toolbar-title>{{ isEdit ? 'Editar' : 'Crear' }} perímetro</v-toolbar-title>
        </v-toolbar>

        <v-card-text
          class="pt-4"
        >
          <v-form
            ref="form"
            v-model="isValid"
          >
            <v-text-field
              id="nombre"
              v-model="nombre"
              name="nombre"
              label="Nombre*"
              outlined
              :rules="[globalRules.required]"
            />
            <v-textarea
              v-model="descripcion"
              label="Descripción*"
              outlined
              :rules="[globalRules.required]"
            />
            <vx-date-text-edit
              v-model="fecha"
              label="Fecha*"
              outlined
              required
            />
          </v-form>
          <v-divider
            class="mb-8"
          />

          <div>
            <div
              class="graphics-header"
            >
              <div
                class="graphics-title"
              >
                <v-icon
                  class="mr-2"
                >
                  mdi-vector-square
                </v-icon>
                <span
                  class="text-subtitle-2 font-weight-medium"
                >Perímetro</span>
              </div>

              <v-chip
                small
                class="graphics-total"
                :class="{ 'is-empty': graphicList.length === 0 }"
                label
              >
                Perímetros: {{ graphicList.length }}
              </v-chip>
            </div>

            <v-btn
              class="graphics-btn"
              block
              outlined
              color="primary"
              @click="drawGraphicsOnMap"
            >
              <v-icon
                left
              >
                mdi-draw-pen
              </v-icon>
              {{ isEdit ? 'Editar perímetro' : 'Crear perímetro' }}
            </v-btn>

            <v-divider
              class="my-6"
            />

            <div
              class="upload-title"
            >
              <v-icon
                class="mr-2"
              >
                mdi-file-upload-outline
              </v-icon>
              <span
                class="text-subtitle-2 font-weight-medium"
              >Importar</span>
            </div>

            <div
              class="upload-row mt-3"
            >
              <v-file-input
                v-model="perimetroKML"
                label="KML (.kml)"
                dense
                outlined
                accept=".kml"
                hide-details
                class="upload-input"
              />
              <v-btn
                class="upload-btn"
                outlined
                color="primary"
                :disabled="!perimetroKML"
                @click="subirPerimetro('KML')"
              >
                Subir
              </v-btn>
            </div>

            <div
              class="upload-row mt-3"
            >
              <v-file-input
                v-model="perimetroSHP"
                label="SHP (ZIP sin carpetas)"
                dense
                outlined
                accept=".zip"
                hide-details
                class="upload-input"
              />
              <v-btn
                class="upload-btn"
                outlined
                color="primary"
                :disabled="!perimetroSHP"
                @click="subirPerimetro('SHP')"
              >
                Subir
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="isEdit"
            text
            color="red"
            @click="deletePerimetro"
          >
            Borrar
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="red"
            @click="cerrar"
          >
            Cancelar
          </v-btn>
          <v-btn
            text
            color="green"
            :disabled="!isValid || graphicList.length === 0 || !aceptarCambios"
            @click="aceptar"
          >
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-snackbar
        v-model="showSnackBar"
        :color="snackbarColor"
        :timeout="3000"
      >
        <v-icon>mdi-alert</v-icon>
        {{ snackbarText }}
      </v-snackbar>
    </v-dialog>
  </div>
</template>

<script>
import * as ArcGIS from '../../../helpers/ArcGIS'

export default {
  props: {
    show: Boolean,
    idIncendio: String,
    perimetroSelected: Object,
    isEdit: Boolean
  },

  data: () => ({
    showDialog: false,

    isValid: false,

    nombre: null,
    descripcion: null,
    fecha: null,

    graphicList: [],
    perimetroKML: null,
    perimetroSHP: null,

    aceptarCambios: false,
    nombreViejo: null,

    showSnackBar: false,
    snackbarColor: null,
    snackbarText: null
  }),

  watch: {
    show() {
      this.showDialog = this.show

      if (this.show) {
        if (this.isEdit) {
          this.nombre = this.perimetroSelected.NOMBRE
          this.descripcion = this.perimetroSelected.DESCRIPCION
          this.fecha = this.$date.formatDate(this.perimetroSelected.FECHA, 'DD/MM/YYYY HH:mm')

          this.graphicList = JSON.parse(JSON.stringify([...this.perimetroSelected.GRAFICOS_PERIMETRO]))
        } else {
          this.fecha = this.$date.currentDate()
        }
      }
    },

    // Watchers para habilitar el botón de aceptar en crear/editar perímetro
    nombre(nuevoNombre) {
      this.aceptarCambios = this.aceptarEdicion()
    },

    descripcion(nuevaDescripcion) {
      this.aceptarCambios = this.aceptarEdicion()
    },

    graphicList(nuevaGraphicList) {
      this.aceptarCambios = this.aceptarEdicion()
    }
  },

  methods: {
    async subirPerimetro(formato) {
      try {
        const perimetro = formato === 'KML' ? this.perimetroKML : this.perimetroSHP
        const response = await ArcGIS.fileToGeoJSON(perimetro, formato)
        const graficosNormalizados = response.map(g => {
          let geometry = g.geometry

          if (geometry?.rings) {
            geometry = geometry.rings[0]
          } else if (geometry?.paths) {
            geometry = geometry.paths[0]
          } else if (geometry?.x !== undefined && geometry?.y !== undefined) {
            geometry = [geometry.x, geometry.y]
          }

          return {
            ...g,
            geometry
          }
        })

        this.graphicList.push(...graficosNormalizados)
        this.snackbarSuccess('Perímetro importado correctamente.')
      } catch (error) {
        console.error('Error al subir perímetro:', error)
        this.snackbarError('No se pudo importar el perímetro. Asegúrate de subir un ZIP sin carpetas con los archivos .shp, .dbf, .shx y .prj.')
      }
    },

    openUploadPerimeterDialog() {
      this.showDialog = true
    },

    drawGraphicsOnMap() {
      this.showDialog = false
      this.$eventHub.$emit('showPerimeterSymbology', { // Vamos a la clase PerimeterSymbology donde hacemos referencia a showPerimeterSymbology
        callback: this.onDrawGraphicsEnded,
        graphics: JSON.parse(JSON.stringify(this.graphicList))
      })
    },

    onDrawGraphicsEnded(graphics) {
      this.showDialog = true
      this.graphicList = graphics
    },

    aceptar() {
      if (!this.isEdit) { // Cuando se crea un perímetro
        this.addPerimetro()
      } else { // Cuando se edita un perímetro ya existente
        this.editPerimetro()
      }
      this.cerrar()
    },

    async addPerimetro() {
      try {
        const date = this.$date.parseDate(this.fecha)
        let newPerimetro = {
          ID_PERIMETRO: this.$uuid.createUUID(),
          ID_INCENDIO: this.idIncendio,
          NOMBRE: this.nombre,
          DESCRIPCION: this.descripcion,
          GRAFICOS_PERIMETRO: this.graphicList,
          FECHA: date
        }
        newPerimetro = this.modelGraphicsList(newPerimetro)
        newPerimetro.measure = await ArcGIS.measurePerimetros(newPerimetro.GRAFICOS_PERIMETRO)
        this.$store.dispatch('incendio/addPerimetro', newPerimetro)
      } catch (error) {
        console.error('Error al crear perímetro: ', error)
        this.$notify('Error al crear perímetro')
      }
    },

    async editPerimetro() {
      try {
        const date = this.$date.parseDate(this.fecha)
        let editPerimetro = {
          ID_PERIMETRO: this.perimetroSelected.ID_PERIMETRO,
          ID_INCENDIO: this.idIncendio,
          NOMBRE: this.nombre,
          DESCRIPCION: this.descripcion,
          GRAFICOS_PERIMETRO: this.graphicList,
          FECHA: date
        }
        editPerimetro = this.modelGraphicsList(editPerimetro)
        editPerimetro.measure = await ArcGIS.measurePerimetros(editPerimetro.GRAFICOS_PERIMETRO)
        this.$store.dispatch('incendio/editPerimetro', editPerimetro)
      } catch (error) {
        console.error('Eror al editar perímetro: ', error)
        this.$notify('Error al editar perímetro')
      }
    },

    deletePerimetro() { // Método para borrar el perímetro
      const msg = `Borrando perímetro: ${this.perimetroSelected.NOMBRE}`

      this.$root.$confirmDialog.open('¿Está seguro?', msg).then(result => {
        if (result) {
          ArcGIS.removeGraphicsPerimetro(this.perimetroSelected.GRAFICOS_PERIMETRO)
          this.$store.dispatch('incendio/deletePerimetro', this.perimetroSelected)

          this.cerrar()
        }
      })
    },

    modelGraphicsList(perimetro) {
      perimetro.GRAFICOS_PERIMETRO = perimetro.GRAFICOS_PERIMETRO.map(g => {
        const sourceAttributes = g.attributes || g.ATRIBUTOS
        const sourceSymbol = g.symbol || g.SIMBOLO
        const sourceGeometry = g.geometry || g.GEOMETRIA

        const finalAttributes = typeof sourceAttributes === 'string' ? JSON.parse(sourceAttributes) : sourceAttributes
        const finalSymbol = typeof sourceSymbol === 'string' ? JSON.parse(sourceSymbol) : sourceSymbol
        const finalGeometry = typeof sourceGeometry === 'string' ? JSON.parse(sourceGeometry) : sourceGeometry

        return {
          ID_GRAFICOS_PERIMETRO: finalAttributes.ID_GRAFICO || this.$uuid.createUUID(),
          ID_PERIMETRO: perimetro.ID_PERIMETRO,
          GEOMETRIA: JSON.stringify(finalGeometry),
          SIMBOLO: JSON.stringify(finalSymbol),
          ATRIBUTOS: JSON.stringify(finalAttributes),
          TIPO: (g.type || g.TIPO)
        }
      })

      return perimetro
    },

    resetVariables() { // Método que resetea todas las variables utilizadas
      this.nombre = null
      this.descripcion = null
      this.fecha = null
      this.graphicList = []
      this.perimetroKML = null
      this.perimetroSHP = null
      this.$refs.form.reset()
    },

    cerrar() { // Método que además de resetear, cierra la ventana cuando pulses el botón donde sea asignado
      this.resetVariables()
      this.$emit('close')
    },

    aceptarEdicion() {
      /* CASOS DE USO
      Crear perímetro:
        El botón Aceptar aparecerá desactivado hasta que se rellene el campo nombre y descripción y se haya dibujado o importado como mínimo un perímetro.

      Editar perímetro:
        El botón Aceptar está desactivado si no se ha realizado ningún cambio. Si el nombre o la descripción cambian, el botón se activará (si los dejas vacíos te exige que los rellenes).
        Si modificas un perímetro ya creado o dibujas o importas uno nuevo, el botón se activará. Tiene que quedar como mínimo un perímetro, sino no se activará el botón.
      */
      if (this.isEdit) {
        return (this.perimetroSelected.NOMBRE !== this.nombre || this.perimetroSelected.DESCRIPCION !== this.descripcion ||
        JSON.stringify(this.perimetroSelected.GRAFICOS_PERIMETRO) !== JSON.stringify(this.graphicList))
      }
      return true
    },

    // Métodos para mostrar la notificación cuando importas un perímetro
    snackbarError(msg) {
      this.showSnackBar = true
      this.snackbarColor = 'error'
      this.snackbarText = msg
    },
    snackbarSuccess(msg) {
      this.showSnackBar = true
      this.snackbarColor = 'success'
      this.snackbarText = msg
    }

  }
}
</script>

<style scoped>
.graphics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.graphics-title {
  display: flex;
  align-items: center;
}

.graphics-btn {
  text-transform: none;
  letter-spacing: 0;
}

.upload-title {
  display: flex;
  align-items: center;
}

.upload-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.upload-input {
  flex: 1;
}

.upload-btn {
  min-width: 96px;
  height: 40px;
  text-transform: none;
  letter-spacing: 0;
}
</style>
